import { useEffect } from 'react';
import { company } from '../data/company';
import {
  SITE_URL,
  DEFAULT_OG,
  DEFAULT_OG_WIDTH,
  DEFAULT_OG_HEIGHT,
  absoluteUrl,
} from '../lib/seo';

/**
 * Dependency-free document-head manager for the public marketing site. Renders nothing; on
 * mount/update it upserts title / description / canonical / robots / Open Graph / Twitter tags and
 * injects per-page JSON-LD (removed on unmount so structured data never leaks between routes).
 *
 * THE CANONICAL URL IS THE POINT OF THIS COMPONENT. The static <head> in index.html is shared by
 * every route, so it deliberately carries NO canonical link: one shared canonical would either name
 * the wrong page or, as it did until now, name a different HOST — the shipped shell declared
 * `canonical = https://bio.airmax.co.in/` on every url of greenbaybio.com, which is an instruction to
 * index the other site instead. Per-route canonicals are set here (Google renders the page, so it
 * reads them) and written statically into the pre-rendered html by `npm run seo:build`, which is what
 * crawlers that do not run JavaScript read.
 */
export interface SeoProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  type?: 'website' | 'article' | 'product';
  noIndex?: boolean;
  keywords?: string[];
  jsonLd?: Record<string, unknown>[];
  /**
   * Path to a Markdown mirror of this page's factual content (product pages have one). Advertised as
   * `<link rel="alternate" type="text/markdown">` so an agent that prefers plain text can fetch the
   * facts without parsing the page.
   */
  markdownPath?: string;
  /** ISO date this page's content last changed, where the source records one. */
  updated?: string;
}

const MD_SELECTOR = 'link[rel="alternate"][type="text/markdown"]';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${CSS.escape(key)}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(attr: 'name' | 'property', key: string) {
  document.head.querySelector(`meta[${attr}="${CSS.escape(key)}"]`)?.remove();
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function Seo({
  title,
  description,
  path = '/',
  image = DEFAULT_OG,
  type = 'website',
  noIndex = false,
  keywords,
  jsonLd = [],
  markdownPath,
  updated,
}: SeoProps) {
  const jsonLdKey = JSON.stringify(jsonLd);
  const keywordKey = (keywords ?? []).join('|');
  useEffect(() => {
    const fullTitle = title.includes(company.name) ? title : `${title} | ${company.name}`;
    const desc = (description ?? '').replace(/\s+/g, ' ').trim().slice(0, 300);
    const pathname = path.split('?')[0];
    const canonical = absoluteUrl(pathname);
    const ogImage = absoluteUrl(image);
    const isDefaultCard = image === DEFAULT_OG;

    document.title = fullTitle;
    document.documentElement.setAttribute('lang', 'en');
    upsertMeta('name', 'description', desc);
    if (keywords?.length) upsertMeta('name', 'keywords', keywords.join(', '));
    // A page that stops supplying keywords must not inherit the previous route's.
    else removeMeta('name', 'keywords');
    upsertMeta('name', 'robots', noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');
    upsertMeta('name', 'googlebot', noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');
    upsertLink('canonical', canonical);

    upsertMeta('property', 'og:site_name', company.name);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', desc);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('property', 'og:image:alt', fullTitle);
    upsertMeta('property', 'og:locale', 'en_IN');
    // Dimensions let a scraper lay the card out before the image arrives. Only the default card's
    // size is known here; a product photo's is not, so nothing is asserted for it.
    if (isDefaultCard) {
      upsertMeta('property', 'og:image:width', String(DEFAULT_OG_WIDTH));
      upsertMeta('property', 'og:image:height', String(DEFAULT_OG_HEIGHT));
    } else {
      removeMeta('property', 'og:image:width');
      removeMeta('property', 'og:image:height');
    }
    if (updated) upsertMeta('property', 'og:updated_time', updated);
    else removeMeta('property', 'og:updated_time');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', desc);
    upsertMeta('name', 'twitter:image', ogImage);
    upsertMeta('name', 'author', company.legalName);

    document.head.querySelectorAll(MD_SELECTOR).forEach((el) => el.remove());
    if (markdownPath) {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('type', 'text/markdown');
      link.setAttribute('href', absoluteUrl(markdownPath));
      document.head.appendChild(link);
    }

    const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    const scripts = items.map((item) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo-jsonld', '');
      s.text = JSON.stringify(item);
      document.head.appendChild(s);
      return s;
    });
    return () => scripts.forEach((s) => s.remove());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, image, type, noIndex, jsonLdKey, keywordKey, markdownPath, updated]);

  return null;
}

export const SITE_URL_EXPORT = SITE_URL;
